/**
 * Share Template Generation Logic
 *
 * Pure functions for generating viral share templates based on analysis results.
 * Extracted from ViralShareButton.tsx (Line Count Refactor 2025-11-21)
 */
import type {
	DramaAnalysisResponse,
	MessageContext,
} from "@dramadetector/shared";
import { DRAMA_SCORE_THRESHOLD } from "../generateEpisodeTitle";

/**
 * Format issue type for display (e.g., "information-gathering" → "Information Gathering")
 * Matches the formatting used in IssuesSection.tsx for consistency
 */
function formatIssueType(raw: string): string {
	return raw
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase → spaced
		.replace(/[-_]+/g, " ") // kebab/snake → spaced
		.replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case
}

/**
 * Share template types based on character voices and drama score
 */
export type ShareTemplateType =
	| "episode_framing"
	| "pattern_recognition"
	| "advice_wisdom"
	| "reality_tv_commentary";

export type ShareSubjectType = "issue" | "advice" | "summary";

export interface ShareSubject {
	id: string;
	type: ShareSubjectType;
	title: string;
	subtitle?: string;
	body: string;
	sourceIndex?: number;
	score?: number;
}

export interface ShareTemplate {
	id: string;
	type: ShareTemplateType;
	voice: "narrator" | "expert";
	title: string;
	description: string;
	shareText: string;
	hashtags: string[];
}

function normalizeId(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");
}

function extractRewriteBody(rewrite: unknown): string | null {
	if (!rewrite || typeof rewrite !== "object") return null;
	const record = rewrite as Record<string, unknown>;
	const candidates = [
		record.improved_message,
		record.rewrite,
		record.rewritten,
		record.after,
		record.suggestion,
		record.text,
		record.content,
	];

	for (const candidate of candidates) {
		if (typeof candidate === "string" && candidate.trim().length > 0) {
			return candidate.trim();
		}
	}

	return null;
}

export function getVisibleShareSubjects(
	analysis: DramaAnalysisResponse,
): ShareSubject[] {
	const subjects: ShareSubject[] = [];
	const score = analysis.result?.score ?? analysis.score ?? 0;
	const issues = analysis.result?.issues ?? analysis.issues ?? [];
	const rewrites = analysis.result?.rewrites ?? analysis.rewrites ?? [];

	issues.slice(0, 3).forEach((issue, index) => {
		const title = formatIssueType(
			issue?.raw_type || issue?.type || `communication pattern ${index + 1}`,
		);
		const body =
			(typeof issue?.description === "string" && issue.description.trim()) ||
			title;

		subjects.push({
			id: normalizeId(title || `issue_${index + 1}`),
			type: "issue",
			title,
			subtitle: index === 0 ? "Most relevant" : "Available to share",
			body,
			sourceIndex: index,
			score,
		});
	});

	const rewriteBody = extractRewriteBody(rewrites[0]);
	if (rewriteBody) {
		subjects.push({
			id: "advice_takeaway",
			type: "advice",
			title: "Advice Takeaway",
			subtitle: "Based on your rewrite guidance",
			body: rewriteBody,
			sourceIndex: 0,
			score,
		});
	}

	subjects.push({
		id: "score_summary",
		type: "summary",
		title: "Score Summary",
		subtitle: "Always available",
		body: `Your drama score is ${score.toFixed(1)} / 10.`,
		score,
	});

	return subjects;
}

/**
 * Generate share templates based on one selected share subject.
 */
export function generateShareTemplatesForSubject(
	subject: ShareSubject,
	analysis: DramaAnalysisResponse,
	context: MessageContext | undefined,
	t: (key: string, params?: Record<string, unknown>) => string,
): ShareTemplate[] {
	const templates: ShareTemplate[] = [];
	const score = analysis.result?.score ?? analysis.score ?? 0;

	if (score >= DRAMA_SCORE_THRESHOLD.MEDIUM) {
		templates.push({
			id: "episode_framing",
			type: "episode_framing",
			voice: "narrator",
			title: t("share.templates.episode_framing.title"),
			description: t("share.templates.episode_framing.description"),
			shareText: t("share.templates.episode_framing.text", {
				score: score.toFixed(1),
				context: context ? ` ${context}` : "",
			}),
			hashtags: ["#DramaDetector", "#EpisodeOfMyLife"],
		});
	}

	if (subject.type === "issue") {
		templates.push({
			id: "pattern_recognition",
			type: "pattern_recognition",
			voice: "expert",
			title: t("share.templates.pattern_recognition.title"),
			description: t("share.templates.pattern_recognition.description"),
			shareText: t("share.templates.pattern_recognition.text", {
				pattern: subject.title,
			}),
			hashtags: ["#DramaDetector", "#CalledOut"],
		});
	}

	if (subject.type === "advice" || score < DRAMA_SCORE_THRESHOLD.LOW) {
		templates.push({
			id: "advice_wisdom",
			type: "advice_wisdom",
			voice: "expert",
			title: t("share.templates.advice_wisdom.title"),
			description: t("share.templates.advice_wisdom.description"),
			shareText:
				subject.type === "advice"
					? subject.body
					: t("share.templates.advice_wisdom.text"),
			hashtags: ["#DramaDetector", "#RealTalk"],
		});
	}

	if (
		score >= DRAMA_SCORE_THRESHOLD.LOW &&
		score < DRAMA_SCORE_THRESHOLD.MEDIUM
	) {
		templates.push({
			id: "reality_tv_commentary",
			type: "reality_tv_commentary",
			voice: "narrator",
			title: t("share.templates.reality_tv_commentary.title"),
			description: t("share.templates.reality_tv_commentary.description"),
			shareText: t("share.templates.reality_tv_commentary.text", {
				score: score.toFixed(1),
			}),
			hashtags: ["#DramaDetector", "#NarratorVoice"],
		});
	}

	templates.push({
		id: "basic_share_results",
		type: "advice_wisdom",
		voice: "expert",
		title: t("share.templates.basic.title"),
		description: t("share.templates.basic.description"),
		shareText:
			subject.type === "summary"
				? subject.body
				: t("share.templates.basic.text", {
						score: score.toFixed(1),
					}),
		hashtags: ["#DramaDetector"],
	});

	return templates;
}

/**
 * Backwards-compatible wrapper using the recommended first visible subject.
 */
export function generateShareTemplates(
	analysis: DramaAnalysisResponse,
	context: MessageContext | undefined,
	t: (key: string, params?: Record<string, unknown>) => string,
): ShareTemplate[] {
	const [defaultSubject] = getVisibleShareSubjects(analysis);

	if (!defaultSubject) {
		return [
			{
				id: "basic_share_results",
				type: "advice_wisdom",
				voice: "expert",
				title: t("share.templates.basic.title"),
				description: t("share.templates.basic.description"),
				shareText: t("share.templates.basic.text", {
					score: (analysis.result?.score ?? analysis.score ?? 0).toFixed(1),
				}),
				hashtags: ["#DramaDetector"],
			},
		];
	}

	return generateShareTemplatesForSubject(defaultSubject, analysis, context, t);
}

/**
 * Truncate text to prevent layout breakage on fixed 1080×1920px share cards
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum character length
 * @returns Truncated text with ellipsis if exceeded
 */
export function truncateText(text: string, maxLength: number): string {
	if (text.length <= maxLength) return text;
	return text.substring(0, maxLength - 3) + "...";
}
